/**
 * SSE Event Stream Hook
 *
 * Manages Server-Sent Events connection and dispatches to TanStack Query
 */

import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { CommanderEvent } from '../types/events';
import { equipmentKeys } from '../hooks/useEquipment';
import { sessionKeys } from '../hooks/useSessions';
import { topologyKeys } from '../hooks/useTopology';

const SSE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/events`
  : '/api/events';

const RECONNECT_DELAY = Number(import.meta.env.VITE_SSE_RECONNECT_DELAY) || 3000;

export interface UseEventStreamOptions {
  /** Whether the event stream is enabled */
  enabled?: boolean;
  /** Callback when an event is received */
  onEvent?: (event: CommanderEvent) => void;
  /** Callback when connection is established */
  onConnect?: () => void;
  /** Callback when connection is lost */
  onDisconnect?: () => void;
  /** Callback when an error occurs */
  onError?: (error: Event) => void;
}

/**
 * Hook to manage SSE connection and invalidate queries on events
 *
 * @example
 * ```tsx
 * function App() {
 *   useEventStream({
 *     enabled: true,
 *     onEvent: (event) => console.log('Received:', event),
 *   });
 *
 *   return <Dashboard />;
 * }
 * ```
 */
export function useEventStream(options: UseEventStreamOptions = {}) {
  const {
    enabled = true,
    onEvent,
    onConnect,
    onDisconnect,
    onError,
  } = options;

  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleEvent = useCallback(
    (event: CommanderEvent) => {
      // Call user callback first
      onEvent?.(event);

      // Invalidate relevant queries based on event type
      switch (event.type) {
        case 'stun.completed':
        case 'unstun.completed':
        case 'radio_check.completed':
          // Delay invalidation so the RadioCommandConsumer has time to
          // persist the updated stun state before the frontend re-fetches.
          setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
            queryClient.invalidateQueries({ queryKey: topologyKeys.data() });
          }, 1500);
          break;

        case 'command.progress':
          // Progress events are forwarded via onEvent callback only —
          // no query invalidation needed for intermediate stages.
          break;

        case 'session.created':
        case 'session.ended':
          queryClient.invalidateQueries({ queryKey: sessionKeys.active() });
          queryClient.invalidateQueries({ queryKey: sessionKeys.history() });
          queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
          break;

        case 'presence.updated':
          queryClient.invalidateQueries({ queryKey: topologyKeys.data() });
          break;

        case 'equipment.updated':
          queryClient.invalidateQueries({ queryKey: equipmentKeys.lists() });
          break;
      }
    },
    [queryClient, onEvent]
  );

  const connect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const eventSource = new EventSource(SSE_URL);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      onConnect?.();
    };

    eventSource.onmessage = (messageEvent) => {
      try {
        const data = JSON.parse(messageEvent.data) as CommanderEvent;
        handleEvent(data);
      } catch (error) {
        console.error('Failed to parse SSE event:', error);
      }
    };

    eventSource.onerror = (error) => {
      onError?.(error);
      onDisconnect?.();

      // Close current connection
      eventSource.close();
      eventSourceRef.current = null;

      // Attempt reconnection
      reconnectTimeoutRef.current = setTimeout(connect, RECONNECT_DELAY);
    };
  }, [handleEvent, onConnect, onDisconnect, onError]);

  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [enabled, connect]);

  // Return current connection state
  return {
    isConnected: eventSourceRef.current?.readyState === EventSource.OPEN,
  };
}
